import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, User, Mail, Shield, Trash2, Key, Eye, Copy, EyeOff, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// Mobile reveal subcomponent
const MobileReveal = ({ password, onCopy, userId }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="inline-flex items-center gap-2">
      <span className="px-2 py-1 rounded bg-slate-100 font-mono text-sm select-all">
        {revealed ? (
          <span data-testid={`password-plain-${userId}`}>{password}</span>
        ) : (
          '••••••••'
        )}
      </span>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRevealed((v) => !v)} title={revealed ? 'Hide' : 'Reveal'}>
        {revealed ? <EyeOff className="h-4 w-4 text-slate-600" /> : <Eye className="h-4 w-4 text-slate-600" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCopy} title="Copy">
        <Copy className="h-4 w-4 text-slate-600" />
      </Button>
    </div>
  );
};

const UsersList = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkResultsDialogOpen, setBulkResultsDialogOpen] = useState(false);


  const [selectedUser, setSelectedUser] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'booker'
  });
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.matchMedia('(hover: none)').matches);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text || '';
      // Avoid scrolling to bottom
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2rem';
      textArea.style.height = '2rem';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      return false;
    }
  };

  const copyToClipboard = async (text) => {
    const value = text || '';
    if (!value) {
      toast.error('Nothing to copy');
      return;
    }
    // Prefer modern API when available in secure contexts
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(value);
        toast.success('Copied');
        return;
      } catch (e) {
        // Fall through to legacy
      }
    }
    // Fallback for non-secure contexts / unsupported browsers
    const ok = fallbackCopyTextToClipboard(value);
    if (ok) {
      toast.success('Copied');
    } else {
      toast.error('Copy failed');
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (userId, userName) => {
    if (userId === currentUser.id) {
      toast.error('You cannot delete yourself');
      return;
    }
    setUserToDelete({ id: userId, name: userName });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await axiosInstance.delete(`${API}/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setGeneratedPassword('');
    setPasswordDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      role: user.role
    });
    setEditDialogOpen(true);
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axiosInstance.put(`${API}/users/${selectedUser.id}`, editFormData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }

    try {
      const response = await axiosInstance.put(`${API}/users/${selectedUser.id}/password`, {
        new_password: newPassword
      });
      setGeneratedPassword(newPassword);
      toast.success('Password reset successfully');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  const filteredUsers = users.filter((user) =>
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-900/20 text-red-400 border-red-700';
      case 'om':
        return 'bg-purple-900/20 text-purple-400 border-purple-700';
      case 'leader':
        return 'bg-blue-900/20 text-blue-400 border-blue-700';
      case 'booker':
        return 'bg-green-900/20 text-green-400 border-green-700';
      default:
        return 'bg-slate-800 text-slate-300 border-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-slate-300">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-2xl font-bold title-gold">All Users</h2>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full max-w-md md:w-80">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search users..."
              className="pl-10 input-dark"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-users-input"
            />
          </div>
          {/* Only admin can force reset all passwords */}
          {currentUser.role === 'admin' && (
            <Button
              onClick={() => setForceDialogOpen(true)}
              className="btn-red"
              data-testid="force-reset-all-btn"
              title="Force reset all user passwords"
            >
              <Key className="h-4 w-4 mr-2" /> Force Reset All
            </Button>
          )}
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <Card className="beast-card">
          <CardContent className="py-12 text-center">
            <p className="text-slate-300" data-testid="no-users-msg">
              {searchTerm ? 'No users found matching your search.' : 'No users added yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="beast-card hover:shadow-2xl hover:shadow-yellow-900/20 transition-shadow duration-200" data-testid={`user-card-${user.id}`}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <User className="h-5 w-5 title-gold" />
                    <span className="text-slate-100" data-testid={`user-name-${user.id}`}>{user.full_name}</span>
                  </span>
                  <div className="flex gap-1">
                    {/* Only show edit/delete buttons if not OM viewing admin users */}
                    {!(currentUser.role === 'om' && (user.role === 'admin' || user.role === 'om')) && (
                      <>
                        <Button
                          onClick={() => openEditDialog(user)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-slate-800"
                          data-testid={`edit-user-btn-${user.id}`}
                          title="Edit User"
                        >
                          <Edit2 className="h-4 w-4 text-yellow-600" />
                        </Button>
                        <Button
                          onClick={() => openPasswordDialog(user)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-slate-800"
                          data-testid={`view-password-btn-${user.id}`}
                          title="Reset Password"
                        >
                          <Key className="h-4 w-4 text-blue-600" />
                        </Button>
                        {currentUser.id !== user.id && (
                          <Button
                            onClick={() => openDeleteDialog(user.id, user.full_name)}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-slate-800"
                            data-testid={`delete-user-btn-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </>
                    )}
                {user.plain_password ? (
                  <div className="mt-2">
                    <div className="text-xs text-slate-400 mb-1">Password</div>
                    {/* Desktop hover-to-reveal */}
                    {!isMobile ? (
                      <div className="group inline-flex items-center gap-2">
                        <span className="px-2 py-1 rounded bg-slate-100 font-mono text-sm select-all">
                          <span className="group-hover:hidden">••••••••</span>
                          <span className="hidden group-hover:inline" data-testid={`password-plain-${user.id}`}>{user.plain_password}</span>
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => copyToClipboard(user.plain_password)}
                          title="Copy password"
                          data-testid={`copy-password-${user.id}`}
                        >
                          <Copy className="h-4 w-4 text-slate-600" />
                        </Button>
                      </div>
                    ) : (
                      // Mobile tap-to-reveal
                      <MobileReveal password={user.plain_password} onCopy={() => copyToClipboard(user.plain_password)} userId={user.id} />
                    )}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-amber-600">Password not available yet</div>
                )}

                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Mail className="h-4 w-4 flex-shrink-0 title-gold" />
                  <span className="break-all" data-testid={`user-email-${user.id}`}>{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-slate-700 text-yellow-500 text-[10px] font-bold">P</span>
                    <span className="break-all" data-testid={`user-phone-${user.id}`}>{user.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 title-gold flex-shrink-0" />
                  <span 
                    className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getRoleBadgeColor(user.role)}`}
                    data-testid={`user-role-${user.id}`}
                  >
                    {user.role}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="beast-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="title-gold">Delete User?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Are you sure you want to delete <strong className="title-gold">{userToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)} className="btn-gold-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="btn-red">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force Reset All Dialog */}
      <AlertDialog open={forceDialogOpen} onOpenChange={setForceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force Reset All Passwords?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset passwords for all users except you. New passwords will be generated and displayed once. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                try {
                  const res = await axiosInstance.post(`${API}/users/force-reset`);
                  setBulkResults(res.data || []);
                  setForceDialogOpen(false);
                  // Show a dialog with results
                  setBulkResultsDialogOpen(true);
                } catch (e) {
                  toast.error('Bulk reset failed');
                }
              }}
            >
              Reset All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Results Dialog */}
      <Dialog open={bulkResultsDialogOpen} onOpenChange={setBulkResultsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwords Reset</DialogTitle>
            <DialogDescription>Save these passwords now. They will not be shown again.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-auto space-y-2">
            {bulkResults.length === 0 ? (
              <div className="text-slate-600 text-sm">No results</div>
            ) : (
              bulkResults.map((u) => (
                <div key={u.id} className="p-3 border rounded-md bg-slate-50">
                  <div className="text-sm font-semibold">{u.full_name} <span className="text-slate-500">({u.email})</span></div>
                  <div className="mt-1 inline-flex items-center gap-2">
                    <span className="px-2 py-1 rounded bg-white border font-mono text-sm" data-testid={`bulk-pw-${u.id}`}>{u.new_password}</span>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(u.new_password)}>
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="pt-2">
            <Button className="w-full" onClick={() => setBulkResultsDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.full_name}</DialogTitle>
            <DialogDescription>
              Set a new password for this user. They will need to use this password to log in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {!generatedPassword ? (
              <>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      data-testid="new-password-input"
                    />
                    <Button onClick={generateRandomPassword} variant="outline" data-testid="generate-password-btn">
                      Generate
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setPasswordDialogOpen(false)} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleResetPassword} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="reset-password-btn">
                    Reset Password
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 mb-2 font-semibold">Password Reset Successful!</p>
                  <p className="text-sm text-green-700 mb-3">Make sure to save this password. It cannot be retrieved later.</p>
                  <div className="p-3 bg-white border border-green-300 rounded font-mono text-lg text-center select-all" data-testid="generated-password-display">
                    {generatedPassword}
                  </div>
                </div>
                <Button onClick={() => setPasswordDialogOpen(false)} className="w-full">
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User: {selectedUser?.full_name}</DialogTitle>
            <DialogDescription>
              Update user information. Email changes will require the user to log in with the new email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-full-name">Full Name *</Label>
              <Input
                id="edit-full-name"
                type="text"
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                placeholder="e.g., John Smith"
                data-testid="edit-user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                placeholder="user@company.com"
                data-testid="edit-user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                placeholder="e.g., +1 555 123 4567"
                data-testid="edit-user-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">Role *</Label>
              <Select value={editFormData.role} onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}>
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="leader">Team Leader / QA / OM / Coordinator</SelectItem>
                  <SelectItem value="booker">Booker (Read-only)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={() => setEditDialogOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleEditUser} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-user-edit-btn">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersList;
